import { DialogButtonOrder, IProFormStepsDialogOptions, ThemeName } from "@ngt/opms";
import { GetClientAppSettingsResponse, SentrySettings } from "./api/dtos";

export interface ISentrySettings {
    dsn?: string;
    customer: string;
    environment: string;
    sendDefaultPii: boolean;
    enableMetrics: boolean;
    attachStacktrace: boolean;
    debug: boolean;
    application: string;
    release?: string;
    replaysSessionSampleRate: number;
    replaysOnErrorSampleRate: number;
    enableLogs: boolean;
    tracesSampleRate: number;
    enableReportABug: boolean;
}

export interface IAppSettings {
    debug?: boolean;
    particles?: boolean;
    redactUrl: string
    consentParticipantUrl: string;
    bctEmailAddressMatch: string;
    bctMedicalReviewSharedMailbox: string;


    palette: IPaletteSettings;
    client: IClientSettings;
    proForms: IProFormSettings;

    sentry: ISentrySettings;

    ///////////////////////////////////
    // BCTPRSPTVE-62 - TURN OFF DICOM
    ///////////////////////////////////
    enableDicom: boolean;

};

export interface IPaletteSettings {
    primaryLight: string;
    secondaryLight: string;
    primaryDark: string;
    secondaryDark: string;
    errorDark: string;
    errorLight: string;
    greenLight: string;
    greenDark: string;
    orangeLight: string;
    orangeDark: string;
    redLight: string;
    redDark: string;
    blueLight: string;
    blueDark: string;
    overrideTheme?: ThemeName;
};

export interface IClientSettings {
    baseUrl?: string;
    basePath?: string;
};

export interface IProFormSettings {
    dialogOptions: IProFormStepsDialogOptions;
}

const ClientAppSettingsResponse: GetClientAppSettingsResponse | undefined = (window as any).APP_SETTINGS;

const getSentryAppSettingValue = <TData>(
    appSettingProperty: keyof SentrySettings | undefined
): TData | undefined => {
    if (!appSettingProperty) {
        return undefined;
    }
    const value: TData | undefined = ClientAppSettingsResponse?.settings?.sentry?.[appSettingProperty] as TData;
    if (value === undefined || value === null) {
        return undefined;
    }
    return value;
};
const getSentryEnvironmentValue = (
    environmentProperty: string | undefined
): string | undefined => {
    if (!environmentProperty) {
        return undefined;
    }
    const value: string | undefined = import.meta.env[environmentProperty];
    if (value === undefined || value === null || value === "") {
        return undefined;
    }
    return value;
};
const getSentryEnvironmentString = (
    environmentProperty: string | undefined
): string | undefined => {
    return getSentryEnvironmentValue(environmentProperty);
};
const getSentryEnvironmentNumber = (
    environmentProperty: string | undefined
): number | undefined => {
    const valueStr = getSentryEnvironmentValue(environmentProperty);
    if (!valueStr) {
        return undefined;
    }
    try {
        return parseFloat(valueStr);
    }
    catch {
        return undefined;
    }
};
const getSentryEnvironmentBoolean = (
    environmentProperty: string | undefined
): boolean | undefined => {
    const valueStr = getSentryEnvironmentValue(environmentProperty);
    if (!valueStr) {
        return undefined;
    }
    const lowerCase = valueStr.toLowerCase();
    if ( lowerCase === "1" || lowerCase === 'true') {
        return true;
    }
    if (lowerCase === "0" || lowerCase === 'false') {
        return false;
    }
    return undefined;
};
const getSentryString = (
    appSettingProperty: keyof SentrySettings | undefined, 
    environmentProperty: string | undefined
) => {
    const appSettingValue = getSentryAppSettingValue<string>(appSettingProperty);
    if (appSettingValue) {
        return appSettingValue;
    }
    const environmentValue = getSentryEnvironmentString(environmentProperty);
    if (environmentValue) {
        return environmentValue;
    }
    return undefined;
};
const getSentryNumber = (
    appSettingProperty: keyof SentrySettings | undefined, 
    environmentProperty: string | undefined
) => {
    const appSettingValue = getSentryAppSettingValue<number>(appSettingProperty);
    if (appSettingValue !== null && appSettingValue !== undefined) {
        return appSettingValue;
    }
    const environmentValue = getSentryEnvironmentNumber(environmentProperty);
    if (environmentValue !== null && environmentValue !== undefined) {
        return environmentValue;
    }
    return undefined;
};
const getSentryBoolean = (
    appSettingProperty: keyof SentrySettings | undefined, 
    environmentProperty: string | undefined
) => {
    const appSettingValue = getSentryAppSettingValue<boolean>(appSettingProperty);
    if (appSettingValue !== null && appSettingValue !== undefined) {
        return appSettingValue;
    }
    const environmentValue = getSentryEnvironmentBoolean(environmentProperty);
    if (environmentValue !== null && environmentValue !== undefined) {
        return environmentValue;
    }
    return undefined;
};

const dayOfYear = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth()+1;
    const day = date.getDate();

    const N1 = Math.floor(275 * month / 9);
    const N2 = Math.floor((month + 9) / 12);
    const N3 = (1 + Math.floor((year - 4 * Math.floor(year / 4) + 2) / 3));
    const N = N1 - (N2 * N3) + day - 30;
    return N;
}

const params = new URLSearchParams(window.location.search);
const enableValue = `prospectAi-${dayOfYear(new Date())}`

export const AppSettings: IAppSettings = {
    debug: import.meta.env.DEV || params.get('debug') === enableValue,
    particles: params.get('particles') === enableValue,
    redactUrl: 'https://localhost:7896/redact',
    consentParticipantUrl: (window as any).CONSENT_PARTICIPANT_BASE_URL ?? import.meta.env.VITE_CONSENT_PARTICIPANT_BASE_URL,
    bctEmailAddressMatch: (window as any).SAE_NOTIFY?.bctEmailAddressMatch ?? import.meta.env.VITE_BCT_EMAIL_MATCH,
    bctMedicalReviewSharedMailbox: (window as any).SAE_NOTIFY?.bctMedicalReviewSharedMailbox  ?? import.meta.env.VITE_BCT_REVIEW_MAILBOX,


    ///////////////////////////////////
    // BCTPRSPTVE-62 - TURN OFF DICOM
    ///////////////////////////////////
    enableDicom: true,


    client: {
        baseUrl: import.meta.env.VITE_API_URL,
        basePath: '/api'
    },
    palette: {
        // primaryDark: '#7d6699',
        primaryDark: '#57476b',
        primaryLight: '#562c85',
        secondaryDark: '#99567e',
        secondaryLight: '#e8078c',
        // overrideTheme: 'light',
        errorDark: 'rgb(251, 110, 99)',
        errorLight: '#ef5350',
        greenDark: "#3a5c37",
        greenLight: "#9bbd9a",
        orangeDark: "#79491d",
        orangeLight: "#efb485",
        redDark: "#74221f",
        redLight: "#e19798",
        blueDark: "#1e3a8a", 
        blueLight: "#60a5fa"
    },

    proForms: {
        dialogOptions: {
            cancelText: 'Go back to the form',
            skipText: 'Proceed to the next page',
            buttonOrder: DialogButtonOrder.CancelFirst,
            CancelButtonProps: {
                variant: 'contained',
                color: 'inherit'
            },
            SkipButtonProps: {
                variant: 'contained',
                color: 'primary'
            },
            ActionProps: {
                sx: {
                    alignItems: 'stretch',
                    '& > *': {
                        flex: '1 1 auto'
                    }
                }
            }
        }
    },
    sentry: {
        dsn: getSentryString('dsn', 'VITE_SENTRY_DSN'),
        debug: getSentryBoolean('debug', 'VITE_SENTRY_DEBUG') ?? false,
        enableMetrics: getSentryBoolean('enableMetrics', 'VITE_SENTRY_ENABLE_METRICS') ?? false,
        sendDefaultPii: getSentryBoolean('sendDefaultPii', 'VITE_SENTRY_SEND_DEFAULT_PII') ?? false,
        attachStacktrace: getSentryBoolean('attachStacktrace', 'VITE_SENTRY_ATTACH_STACKTRACE') ?? false,
        customer: getSentryString('customer', 'VITE_SENTRY_CUSTOMER') ?? 'Unknown',
        environment:  getSentryString('environment', 'VITE_SENTRY_ENVIRONMENT') ?? 'Development',
        application: getSentryString(undefined, 'VITE_SENTRY_APPLICATION') ?? 'SourceDataVerification',
        release: getSentryString(undefined, 'VITE_SENTRY_RELEASE'),
        enableLogs: getSentryBoolean('enableLogs', 'VITE_SENTRY_ENABLE_LOGS') ?? false,
        replaysOnErrorSampleRate: getSentryNumber('replaysOnErrorSampleRate', 'VITE_SENTRY_REPLAY_ON_ERROR_SAMPLE_RATE') ?? 1.0,
        replaysSessionSampleRate: getSentryNumber('replaysSessionSampleRate', 'VITE_SENTRY_REPLAY_SESSION_SAMPLE_RATE') ?? 0.1,
        tracesSampleRate: getSentryNumber('tracesSampleRate', 'VITE_SENTRY_TRACE_SAMPLE_RATE') ?? 0.2,
        enableReportABug: getSentryBoolean('enableReportABug', 'VITE_SENTRY_ENABLE_REPORT_A_BUG') ?? false
    }
};
